import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router';
import { ONBOARDING_PATH } from '../app/routes';
import { useDayUi } from '../features/day/dayUiStore';
import { GestureHelp } from '../features/day/GestureHelp';
import { useImportantDateNotifications } from '../features/importantDates/useImportantDateNotifications';
import { useCheckinReminders } from '../features/reminder/useCheckinReminders';
import { useHistoryShortcuts } from '../hooks/useHistoryShortcuts';
import { LayoutMode, useLayoutMode } from '../hooks/useMediaQuery';
import { useRouteView, ViewKind } from '../hooks/useRouteView';
import { FLAG_ON, StorageKey } from '../lib/storageKeys';
import { readStorage } from '../lib/safeStorage';
import { DraftBanner } from './DraftBanner';
import styles from './Shell.module.css';
import { Toasts } from './Toasts';
import { TopBar } from './TopBar';
import { useGlobalShortcuts } from './useGlobalShortcuts';

/**
 * App frame: top bar, draft banner, the routed view and the toast area. On a phone the draft
 * banner is the first thing inside the scrolling page, so it does not take screen space for good.
 */
export function Shell() {
  useHistoryShortcuts();
  useGlobalShortcuts();
  useCheckinReminders();
  useImportantDateNotifications();
  const view = useRouteView();
  const narrow = useLayoutMode() === LayoutMode.Narrow;
  // Quick mark survives day/week switches and date changes, but leaving the day and week
  // views (month view, stats, settings …) clears it (2026-10b §4.1).
  useEffect(() => {
    if (view.kind !== ViewKind.Day && view.kind !== ViewKind.Week) {
      useDayUi.getState().quickMarkExit();
    }
  }, [view.kind]);
  if (readStorage(StorageKey.Onboarded) !== FLAG_ON) return <Navigate to={ONBOARDING_PATH} replace />;
  const banner = view.kind === ViewKind.Day && view.date !== null ? <DraftBanner date={view.date} /> : null;
  return (
    <div className={styles.app}>
      <TopBar />
      {banner !== null && !narrow ? banner : null}
      <main className={styles.main}>
        {banner !== null && narrow ? banner : null}
        <Outlet />
      </main>
      <GestureHelp />
      <Toasts />
    </div>
  );
}
